import React, { useMemo, useState } from 'react';
import { styled } from 'baseui';
import { Link } from 'react-router-dom';
import { Skeleton } from 'baseui/skeleton';
import { HeadingMedium } from 'baseui/typography';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { useTenantBranding } from '../../../hooks/useTenantBranding';
import { useEstateGroups } from '../../../hooks/useEstates';
import {
  useArrivingNext, useInsideNow, useNextEvents, usePendingRequests, useTenantPassCounts,
} from '../../../hooks/useTenantDashboard';
import {
  DASHBOARD_PERIODS, formatWhen, headerDate, oldestPendingHint, percentOf, periodLabel, periodWindow, roleLabel,
  updatedAgo, type DashboardPeriod,
} from '../../../utils/tenantDashboard';

// SAM-942 · D2 Tenant (no money), DashTenant of the canvas admin. "By estate / type / user type / hour"
// (GET /business_access/stats?group_by, lambdas #841) come next; "Denied at gate", "By gate" and the real
// hour of entry wait for the gate-event table (Sprint 8, Abel).

const Page = styled('div', {
  backgroundColor: '#f9fafb',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  overflowY: 'auto',
  padding: '16px 30px 60px 30px',
  '@media (max-width: 768px)': { padding: '16px 16px 60px 16px' },
});

const Header = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '20px',
});

const Periods = styled('div', {
  display: 'inline-flex',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: '#fff',
});
const Tenant = styled('div', { display: 'flex', alignItems: 'center', gap: '10px' });
const Initials = styled('span', {
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  backgroundColor: 'var(--tenant-primary, #005488)',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});
const RoleChip = styled('span', {
  fontSize: '12px',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '999px',
  backgroundColor: '#f3f4f6',
  color: '#374151',
});
const Subtitle = styled('div', { fontSize: '13px', color: '#6b7280', marginTop: '2px' });
const Controls = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '20px',
});

const Tiles = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))',
  gap: '16px',
  marginBottom: '24px',
  '@media (max-width: 480px)': { gridTemplateColumns: '1fr' },
});

const Lists = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
  gap: '16px',
});

const Card = styled('div', {
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const CardHead = styled('div', { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', fontSize: '13px' });
const CardTitle = styled('div', { fontSize: '14px', fontWeight: 500, color: '#6b7280' });
const Big = styled('div', { fontSize: '32px', fontWeight: 600, color: '#111827', lineHeight: 1.1 });
const Hint = styled('div', { fontSize: '12px', color: '#9ca3af' });
const Empty = styled('div', { fontSize: '13px', color: '#9ca3af', padding: '8px 0' });
const Row = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '8px 0',
  borderTop: '1px solid #f3f4f6',
  fontSize: '13px',
});
const RowMain = styled('div', { fontWeight: 500, color: '#111827' });
const RowSub = styled('div', { color: '#6b7280' });
const RowWhen = styled('div', { color: '#374151', whiteSpace: 'nowrap' });

const PeriodButton = styled('button', {
  border: 0,
  padding: '7px 14px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
});

const TileSkeleton = Skeleton as any;

const Tile = ({ title, value, hint, loading, to }: {
  title: string;
  value: number | undefined;
  hint?: string;
  loading: boolean;
  to?: string;
}) => {
  const body = (
    <Card data-testid={`tile-${title}`}>
      <CardTitle>{title}</CardTitle>
      {loading ? <TileSkeleton width="60px" height="36px" animation /> : <Big>{value ?? '–'}</Big>}
      {hint && <Hint>{hint}</Hint>}
    </Card>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{body}</Link> : body;
};

const initialsOf = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '·';

export const TenantDashboard = () => {
  const permissions = useUserPermissions();
  const { isPropertyManager, isAdmin, mainEstate } = permissions;
  const { tenantName } = useTenantBranding();
  const { allEstateGroups } = useEstateGroups({ limit: 100 });
  const [period, setPeriod] = useState<DashboardPeriod>('today');
  const [estateGroupId, setEstateGroupId] = useState('');
  const scope = useMemo(
    () => ({
      ...(isPropertyManager && !isAdmin && mainEstate ? { estateId: String(mainEstate) } : {}),
      ...(estateGroupId ? { estateGroupId } : {}),
    }),
    [isPropertyManager, isAdmin, mainEstate, estateGroupId],
  );

  const counts = useTenantPassCounts(period, scope);
  const inside = useInsideNow(scope);
  const pending = usePendingRequests();
  const arriving = useArrivingNext(scope);
  const events = useNextEvents();

  const win = periodWindow(period);
  const c = counts.data;
  const updated = updatedAgo(counts.dataUpdatedAt);

  return (
    <Page>
      <Header>
        <div>
          <HeadingMedium margin={0}>Dashboard</HeadingMedium>
          <Subtitle>{headerDate()}{updated ? ` · updated ${updated}` : ''}</Subtitle>
        </div>
        <Tenant>
          {tenantName && <Initials aria-hidden>{initialsOf(tenantName)}</Initials>}
          {tenantName && <strong>{tenantName}</strong>}
          <RoleChip>{roleLabel(permissions)}</RoleChip>
        </Tenant>
      </Header>

      <Controls>
        <Periods role="group" aria-label="Period">
          {DASHBOARD_PERIODS.map((p) => (
            <PeriodButton
              key={p.id}
              aria-pressed={period === p.id}
              $style={period === p.id
                ? { backgroundColor: 'var(--tenant-primary, #005488)', color: '#fff' }
                : { backgroundColor: '#fff', color: '#111827' }}
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </PeriodButton>
          ))}
        </Periods>
        <select
          aria-label="Estate group"
          value={estateGroupId}
          onChange={(e) => setEstateGroupId(e.target.value)}
          style={{ height: 34, borderRadius: 8, border: '1px solid #e5e7eb', padding: '0 10px', fontSize: 13, backgroundColor: '#fff' }}
        >
          <option value="">All estate groups</option>
          {allEstateGroups.map((g: { id?: number; name?: string }) => (
            <option key={g.id} value={String(g.id)}>{g.name}</option>
          ))}
        </select>
      </Controls>

      <Tiles>
        <Tile
          title={period === 'today' ? 'Expected today' : 'Expected'}
          value={c?.expected}
          hint={period === 'today' ? 'Guest, booking and event passes' : `${periodLabel(win)} · guest, booking and event passes`}
          loading={counts.isLoading}
          to="/invitations"
        />
        <Tile title="Valid passes" value={c?.valid} hint={c ? percentOf(c.valid, c.expected) : undefined} loading={counts.isLoading} to="/invitations" />
        <Tile
          title="Entered"
          value={c?.entered}
          hint={`Checked in or out · started ${period === 'today' ? 'today' : periodLabel(win)}`}
          loading={counts.isLoading}
          to="/invitations"
        />
        <Tile title="Inside now" value={inside.data} hint="Entered, not out yet · includes earlier days" loading={inside.isLoading} to="/invitations" />
        <Tile
          title="Pending requests"
          value={pending.count}
          hint={pending.count > 0 ? oldestPendingHint(pending.oldestDays) : 'Nobody waiting'}
          loading={pending.isLoading}
          to="/users"
        />
      </Tiles>

      <Lists>
        <Card>
          <CardHead>
            <CardTitle>Arriving next</CardTitle>
            <Link to="/invitations">All passes</Link>
          </CardHead>
          <Hint>Today, from now on, and the next 7 days</Hint>
          {arriving.isLoading ? (
            <TileSkeleton width="100%" height="120px" animation />
          ) : !arriving.data?.length ? (
            <Empty>No passes arriving today or in the next 7 days</Empty>
          ) : (
            arriving.data.map((a) => (
              <Row key={`${a.entity || ''}-${a.id}`}>
                <div>
                  <RowMain>
                    {a.entity === 'business_access' ? <Link to={`/invitations?pass=${a.id}`}>{a.guest}</Link> : a.guest}
                  </RowMain>
                  <RowSub>{[a.where, a.what].filter(Boolean).join(' · ')}</RowSub>
                </div>
                <RowWhen>{formatWhen(a.start)}</RowWhen>
              </Row>
            ))
          )}
        </Card>

        <Card>
          <CardHead>
            <CardTitle>Next events</CardTitle>
            <Link to="/eventsad/list">All events</Link>
          </CardHead>
          {events.isLoading ? (
            <TileSkeleton width="100%" height="120px" animation />
          ) : !events.items.length ? (
            <Empty>No upcoming events</Empty>
          ) : (
            events.items.map((e) => (
              <Row key={e.id}>
                <div>
                  <RowMain><Link to={`/eventsad/${e.id}`}>{e.name}</Link></RowMain>
                  {typeof e.capacity === 'number' && <RowSub>{e.capacity} capacity</RowSub>}
                </div>
                <RowWhen>{formatWhen(e.start)}</RowWhen>
              </Row>
            ))
          )}
        </Card>
      </Lists>
    </Page>
  );
};

export default TenantDashboard;
